import React, { useEffect, useState, useRef, useCallback } from "react";
import Layout from "../Layout/Layout";
import styled from "styled-components";
import { getNotificationForUser } from "./utilities/ApiCalls";
import { getTokenDetails } from "./utilities/CommonFunction";
import { ThreeDots } from "react-loader-spinner";
import { useTranslation } from "react-i18next";
import { setPageTitle } from "../utils/titleUtils";
import { useLocation, useHistory } from "react-router-dom/cjs/react-router-dom";
import Constant from "./Constant";
import ProfileImage from "../Componets/ProfileImage";

const NotificationPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [t] = useTranslation();
  const observerRef = useRef(null);
  const loaderRef = useRef(null);
  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    setPageTitle(location.pathname);
  }, [location]);

  const fetchNotifications = async (page = 1, append = false) => {
    try {
      let token = localStorage.getItem("loginDetails");
      let resp = {};
      if (token) resp = getTokenDetails(token);

      const response = await getNotificationForUser({
        userId: resp._id,
        pageNo: page,
      });
      const notificationsData = response.data.notifications || [];
      const pagination = response.pagination || {};

      setHasNextPage(pagination.hasNextPage || false);
      setCurrentPage(page);

      if (append) {
        setNotifications((prev) => [...prev, ...notificationsData]);
      } else {
        setNotifications(notificationsData);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
      setHasNextPage(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const lastNotificationRef = useCallback(
    (node) => {
      if (loading || loadingMore) return;

      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          loadMoreNotifications();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [loading, loadingMore, hasNextPage]
  );

  const loadMoreNotifications = () => {
    if (!hasNextPage || loadingMore) return;
    setLoadingMore(true);
    fetchNotifications(currentPage + 1, true);
  };

  // 🔗 Redirect logic
  const handleNotificationClick = (notif) => {
    if (notif.contentType === "story") {
      history.push(`/story-details/${notif.contentId}`);
    } else if (notif.contentType === "poem") {
      history.push(`/poem-details/${notif.contentId}`);
    } else if (notif.type === "follow") {
      history.push(`/userprofile/${notif.userId._id}`);
    }
  };

  const proxiedUrl = (url) => {
    if (!url || url.includes("story-tent-testing.s3.ap-south-1.amazonaws.com")) return url;
    return `${Constant.apiBasePath}proxy-image?url=${encodeURIComponent(url)}`;
  };

  return (
    <Layout>
      <Root>
        <div className="main-container">
          {loading ? (
            <LoaderContainer>
              <ThreeDots color="var(--color-text)" height={80} width={80} />
            </LoaderContainer>
          ) : (
            <NotificationContainer>
              <Header>{t("Notifications.Notifications")}</Header>
              {notifications.length === 0 ? (
                <NoNotification>{t("No Notifications")}</NoNotification>
              ) : (
                <>
                  {notifications.map((notif, index) => (
                    <NotificationCard
                      key={notif._id}
                      $unread={notif.readStatus !== 1}
                      onClick={() => handleNotificationClick(notif)}
                      ref={
                        index === notifications.length - 1
                          ? lastNotificationRef
                          : null
                      }
                    >
                      <div style={{ marginRight: "20px", flexShrink: 0 }}>
                        <ProfileImage
                          src={notif.userId?.profilePic}
                          name={notif.author?.name || notif.userId?.name || "User"}
                          size={50}
                          alt={notif.author?.name || "User"}
                          proxiedUrl={proxiedUrl}
                          imgStyle={{
                            border: "2px solid var(--color-surface)",
                            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                          }}
                          avatarProps={{
                            border: "2px solid var(--color-surface)",
                            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                          }}
                        />
                      </div>
                      <Info>
                        <UserName>{notif.message}</UserName>
                        {notif.createdAt &&
                          !isNaN(new Date(notif.createdAt).getTime()) && (
                            <Timestamp>
                              {new Date(notif.createdAt).toLocaleString(
                                "en-GB",
                                {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "2-digit",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                }
                              )}
                            </Timestamp>
                          )}
                      </Info>
                      {notif.readStatus !== 1 && <UnreadIndicator />}
                    </NotificationCard>
                  ))}

                  {loadingMore && (
                    <LoadingMore ref={loaderRef}>
                      <ThreeDots
                        color="var(--color-text)"
                        height={40}
                        width={40}
                      />
                    </LoadingMore>
                  )}

                  {!loadingMore && hasNextPage && (
                    <LoadMoreButton onClick={loadMoreNotifications}>
                      Load More
                    </LoadMoreButton>
                  )}

                  {!hasNextPage && notifications.length > 0 && (
                    <EndMessage>No more notifications</EndMessage>
                  )}
                </>
              )}
            </NotificationContainer>
          )}
        </div>
      </Root>
    </Layout>
  );
};

export default NotificationPage;

// ---------------- styled components ------------------

const Root = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  font-family: "Merriweather", serif;
  background-color: transparent; /* Global background usually set in Layout, but ensuring transparency or consistency */

  .main-container {
    width: 100%;
    max-width: 800px;
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-bottom: 40px;
  }
`;

const LoaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 80vh;
`;

const NotificationContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
`;

const Header = styled.h2`
  color: var(--color-primary);
  margin-bottom: 30px;
  font-size: 28px;
  font-weight: 700;
  width: 100%;
  text-align: left;
`;

const NotificationCard = styled.div`
  width: 100%;
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  display: flex;
  align-items: center;
  padding: 20px;
  margin: 8px 0;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  position: relative;
  overflow: hidden;

  /* Highlight unread notifications slightly or just rely on the dot */
  background-color: ${({ $unread }) =>
    $unread ? "var(--color-background)" : "var(--color-surface)"};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: var(--color-text);
  }
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const UserName = styled.p`
  color: var(--color-text);
  font-size: 16px;
  font-weight: 500;
  margin: 0;
  font-family: sans-serif;
  line-height: 1.4;
`;

const Timestamp = styled.span`
  color: var(
    --color-text-tertiary
  ); /* using tertiary or secondary for metadata */
  font-size: 12px;
  margin-top: 5px;
  font-family: sans-serif;
`;

const UnreadIndicator = styled.div`
  width: 10px;
  height: 10px;
  background-color: var(--color-error, #ff4444); /* Or a brand color */
  border-radius: 50%;
  margin-left: 15px;
`;

const NoNotification = styled.p`
  color: var(--color-text);
  font-size: 18px;
  text-align: center;
  width: 100%;
  padding: 40px;
  font-style: italic;
`;

const LoadMoreButton = styled.button`
  background-color: transparent;
  color: var(--color-text);
  border: 1px solid var(--color-border);
  border-radius: 30px;
  padding: 10px 30px;
  margin-top: 30px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
  font-weight: 500;

  &:hover {
    background-color: var(--color-text);
    color: var(--color-surface);
    border-color: var(--color-text);
  }
`;

const LoadingMore = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 20px 0;
`;

const EndMessage = styled.p`
  color: var(--color-text-tertiary);
  margin-top: 20px;
  text-align: center;
  font-size: 14px;
`;
